/**
 * Streaming chat state for the floating Alfie widget.
 *
 * Thread binding is the load-bearing part and follows SearchSidePanel's
 * `panelThreadIdRef` contract exactly (ClickUp 86d45umn5):
 *
 *   1. A NEW chat sends `threadId: undefined` — Hermes creates the thread. It
 *      must never reach for "the user's most recent thread", or the model
 *      answers a weeks-old conversation's hidden history.
 *   2. The id Hermes returns (early `thread` event, or `saved` on older
 *      builds) is bound and reused for every later send in that session.
 *   3. A thread explicitly chosen from history sends with THAT id.
 *   4. "New chat" clears the binding, so the next send is unbound again.
 *
 * Deliberately absent: the intent registry, navigation permission cards and
 * voice escalation that SearchSidePanel carried. The widget is plain chat.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  sendChatMessageStreaming,
  useThreadMessages,
  type FileAttachmentMeta,
  type PlanCardData,
  type ResolvedCitation,
  type StreamDoneData,
  type StreamSavedData,
  type StreamThreadData,
} from '@/api/alfie';
import { markFinanceThread, setBriefSentForThread } from '@/lib/finance-topics';

export interface WidgetMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  citations?: ResolvedCitation[];
  /** Plan cards under a package answer (Hermes `planCards`). */
  planCards?: PlanCardData[];
  timestamp?: string;
  files?: FileAttachmentMeta[];
}

/**
 * A send in finance mode (the Xero screens, or a conversation carried from them). `brief` is the
 * matched topic's brief, present only when the topic changed; `topicId` is recorded once the
 * send succeeds so a follow-up does not send the brief again. See `lib/finance-topics`.
 */
export interface FinanceSend {
  brief: string | null;
  topicId: string | null;
}

/** What `activities.entity_name` records for a finance send: never a brief or an account name. */
const FINANCE_ACTIVITY_PREVIEW = 'Asked about my finances';

interface UseWidgetChatOptions {
  /** Thread the widget is currently showing. `null` = a new chat. */
  threadId: string | null;
  /** Called once when a new chat binds the id Hermes created for it. */
  onThreadBound: (threadId: string) => void;
}

export interface WidgetChat {
  messages: WidgetMessage[];
  isTyping: boolean;
  isLoadingHistory: boolean;
  /** Increments once per completed assistant reply — drives the live region. */
  completedReplies: number;
  /** The bound thread no longer resolves (archived, deleted, or read failed). */
  threadMissing: boolean;
  send: (text: string, fileIds?: string[], finance?: FinanceSend | null) => Promise<void>;
  stop: () => void;
  /** Drop the session's transcript and unbind. Backs "New chat". */
  reset: () => void;
}

export function useWidgetChat({ threadId, onThreadBound }: UseWidgetChatOptions): WidgetChat {
  const queryClient = useQueryClient();

  /** The thread this session is bound to. `null` until Hermes names one. */
  const boundThreadIdRef = useRef<string | null>(threadId);
  /**
   * Which thread's persisted messages to fetch. Stays `null` for a session
   * that bound its own id mid-stream — the streamed turns are already in
   * `localMessages`, and fetching them back would render each one twice.
   */
  const [historyThreadId, setHistoryThreadId] = useState<string | null>(threadId);
  const [localMessages, setLocalMessages] = useState<WidgetMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [completedReplies, setCompletedReplies] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const {
    data: history,
    isLoading: isLoadingHistory,
    isError: historyFailed,
    isSuccess: historyLoaded,
  } = useThreadMessages(historyThreadId);

  /**
   * A stored or selected thread that answers with nothing: the read failed, or
   * it succeeded and the row is gone (archived elsewhere, deleted), because RLS
   * turns a vanished thread into an empty result rather than a 404. Only judged
   * when this session has nothing of its own on screen, so an in-flight turn is
   * never mistaken for a dead thread.
   */
  const threadMissing =
    !!historyThreadId &&
    localMessages.length === 0 &&
    (historyFailed || (historyLoaded && (history?.length ?? 0) === 0));

  // React to a real thread switch (history selection, New chat, restore from
  // localStorage) — but ignore the echo of our own mid-stream bind, which
  // arrives as a prop change with the id we just set ourselves.
  useEffect(() => {
    if (threadId === boundThreadIdRef.current) return;
    abortRef.current?.abort();
    boundThreadIdRef.current = threadId;
    setHistoryThreadId(threadId);
    setLocalMessages([]);
    setIsTyping(false);
  }, [threadId]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const bindThread = useCallback(
    (id?: string | null) => {
      if (!id || boundThreadIdRef.current === id) return;
      boundThreadIdRef.current = id;
      onThreadBound(id);
    },
    [onThreadBound],
  );

  /**
   * "New chat" cannot rely on the `threadId` effect alone: a session whose
   * sends all failed never bound an id, so `threadId` is already `null` and
   * the effect early-returns — leaving the dead transcript on screen. Reset
   * explicitly instead.
   */
  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    boundThreadIdRef.current = null;
    setHistoryThreadId(null);
    setLocalMessages([]);
    setIsTyping(false);
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsTyping(false);
    setLocalMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
  }, []);

  const send = useCallback(
    async (text: string, fileIds?: string[], finance?: FinanceSend | null) => {
      const message = text.trim();
      if (!message || isTyping) return;

      const now = new Date().toISOString();
      const streamingId = `streaming-${Date.now()}`;
      setLocalMessages((prev) => [
        ...prev,
        { id: `user-${Date.now()}`, role: 'user', content: message, timestamp: now },
        { id: streamingId, role: 'assistant', content: '', streaming: true, timestamp: now },
      ]);
      setIsTyping(true);

      const abortController = new AbortController();
      const { signal } = abortController;
      abortRef.current = abortController;

      /**
       * Drops any frame that lands after this send was aborted. A `thread` or
       * `saved` frame arriving once the user has moved to a history thread
       * would otherwise rebind the widget to the conversation they walked away
       * from, and a late `done` would announce an answer that is no longer on
       * screen. `fetch` resolves the abort asynchronously, so the window is
       * real, not theoretical.
       */
      const whileLive =
        <A extends unknown[]>(fn: (...args: A) => void) =>
        (...args: A) => {
          if (signal.aborted) return;
          fn(...args);
        };

      const patchStreaming = (patch: Partial<WidgetMessage>) =>
        setLocalMessages((prev) =>
          prev.map((m) => (m.id === streamingId ? { ...m, ...patch } : m)),
        );

      try {
        await sendChatMessageStreaming(
          {
            // `?? undefined` — never `null`. A new chat must send the key
            // absent so Hermes creates a thread (see contract note above).
            threadId: boundThreadIdRef.current ?? undefined,
            message,
            personaId: 'general',
            fileIds: fileIds?.length ? fileIds : undefined,
            surface: 'widget',
            ...(finance
              ? {
                mode: 'finance_info' as const,
                contextBrief: finance.brief ?? undefined,
                activityPreview: FINANCE_ACTIVITY_PREVIEW,
              }
              : {}),
          },
          // onChunk
          whileLive((chunk: string) =>
            setLocalMessages((prev) =>
              prev.map((m) => (m.id === streamingId ? { ...m, content: m.content + chunk } : m)),
            ),
          ),
          undefined, // onMetadata
          // onDone — `response` is the rewritten final text, with transitional
          // [[cite:N]] tokens already swapped for canonical typed tokens.
          whileLive((data: StreamDoneData) => {
            patchStreaming({
              content: data.response || undefined,
              streaming: false,
              citations: data.citations ?? [],
              planCards: data.planCards,
            });
            setCompletedReplies((n) => n + 1);
          }),
          // onSaved
          whileLive((saved: StreamSavedData) => {
            bindThread(saved?.threadId);
            // Saved = the question (and its brief) reached the thread: record both against it.
            const bound = boundThreadIdRef.current;
            if (finance && bound) {
              markFinanceThread(bound);
              if (finance.brief) setBriefSentForThread(bound, finance.topicId);
            }
            queryClient.invalidateQueries({ queryKey: ['alfie-threads'] });
          }),
          // onError
          (errMsg: string) =>
            patchStreaming({
              content: errMsg || 'Sorry, I ran into a problem. Please try again.',
              streaming: false,
            }),
          signal,
          // onThread — early bind, first SSE frame on newer Hermes builds.
          whileLive((thread: StreamThreadData) => {
            bindThread(thread?.threadId);
            // Marked as soon as the conversation has an id, so opening it on /alfie mid-answer
            // still carries finance mode.
            if (finance && thread?.threadId) markFinanceThread(thread.threadId);
          }),
        );
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
        const notEntitled = (err as { code?: string })?.code === 'alfie_chat_not_entitled';
        patchStreaming({
          content: notEntitled
            ? 'Alfie chat is not included in your current plan. Upgrade to chat with Alfie.'
            : 'Sorry, I ran into a problem. Please try again.',
          streaming: false,
        });
      } finally {
        // Only this send may clear the shared state, and only if it is still
        // the one in flight — an aborted turn settling after the user has
        // already sent into another thread would otherwise null that newer
        // controller and drop its typing indicator.
        if (abortRef.current === abortController) {
          abortRef.current = null;
          setIsTyping(false);
        }
      }
    },
    [bindThread, isTyping, queryClient],
  );

  const historyMessages: WidgetMessage[] = (history ?? []).map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    citations: m.metadata?.citations,
    planCards: m.metadata?.planCards,
    files: m.metadata?.files,
    timestamp: m.created_at,
  }));

  return {
    messages: [...historyMessages, ...localMessages],
    isTyping,
    isLoadingHistory: !!historyThreadId && isLoadingHistory,
    completedReplies,
    threadMissing,
    send,
    stop,
    reset,
  };
}
